// "Group info": who is in a group, and what this person may do about it. The app's
// Group info screen (apps/mobile/src/screens/social/DmGroupInfoScreen.tsx) and its
// "Add people" picker (DmGroupCreateScreen.tsx with a group), on the web, with the
// same routes and bodies.
//
// EVERYONE in the group can leave it. A GROUP ADMIN can also rename it, add people and
// remove a person. Whether this person is an admin comes from the group's own answer
// (`myRole`), never from anything the browser keeps, and the server checks it again on
// every action. The controls are simply not drawn for anyone else.
//
// THE PANEL IS THE SERVER'S LATEST ANSWER. It reads the group when it opens, and rename,
// add and remove each answer with the group as it now is, which replaces what is shown.
// Nothing is changed first and confirmed later. After every change the conversation
// and the list are read again at once (onChanged), so the server's own line ("… added
// …") and a new name show without waiting for the next refresh.
//
// One action at a time: while one is on its way every control is off and the pop-up
// does not close. A refusal is one line: the server's words where it has them. Any
// refusal that is not about what was typed or picked can mean the group changed
// elsewhere (another admin removed that person or filled the group, this person is no
// longer an admin, or no longer in it), so the group is read again before the controls
// come back, and the conversation and the list are read again too.
//
// Making someone an admin and mute stay the app's.
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent, ReactElement } from 'react';
import { LogOut, Pencil, UserMinus, UserPlus, Users } from 'lucide-react';
import { InlineLoader } from '../loading';
import {
  GROUP_LIMITS, messagesAPI, type GroupDetail, type GroupMember, type PickPerson,
} from '../../services/messagesApi';
import MessagesDialog from './MessagesDialog';
import { sameCode } from './messageText';
import { cleanTitle, notAllowedLine } from './NewGroupDialog';
import { Face, FindFailureLine, FindField, ListHeading, PickedChips, Quiet, TickRow } from './PickParts';
import { pickLine, usePeopleFinder } from './usePeopleFinder';

interface GroupInfoDialogProps {
  groupId: string;
  /** The project selected in the top bar: the group is shown only if it is this project's. */
  projectId: number;
  /** The person's own employee code, lower case: which row is "You". */
  me: string;
  onClose: () => void;
  /**
   * The group changed (renamed, people added or removed), or turned out to be no longer
   * this person's: the conversation and the list read again. `detail` is the group as
   * the server now has it, when it answered with it.
   */
  onChanged: (detail: GroupDetail | null) => void;
  /** The person has left the group. */
  onLeft: () => void;
}

type Load = 'loading' | 'ready' | 'missing' | 'unconfirmed' | 'failed';
type Confirm = { kind: 'remove'; member: GroupMember } | { kind: 'leave' };

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;
const messageOf = (err: unknown): string | null => {
  const said = (err as { response?: { data?: { message?: unknown } } } | null)?.response?.data?.message;
  return typeof said === 'string' && said.trim() ? said.trim() : null;
};

export const NOT_AVAILABLE = 'This group is not available.';
const SIGN_IN = 'Your sign-in could not be confirmed.';

/** "1 member", "6 members". */
export const membersLabel = (n: number): string => (n === 1 ? '1 member' : `${n} members`);

/**
 * Why a change was not made, in one line. People who cannot be added are named. A 400
 * or a 403 has the server's own words ("Only a group admin can do this", "A group can
 * have at most 50 people", "Group name is too long (max 60 characters)").
 */
export const actionRefusal = (err: unknown, failed: string, picked: PickPerson[] = []): string => {
  const named = notAllowedLine(err, picked);
  if (named) return named;
  const status = statusOf(err);
  if (status === 404) return NOT_AVAILABLE;
  if (status === 401) return SIGN_IN;
  if (status === 400 || status === 403) return messageOf(err) ?? failed;
  return failed;
};

/**
 * Whether a refusal can mean the group changed elsewhere, so the panel, the conversation
 * and the list are read again. Not for a name the server would not take (every 400 on a
 * rename is about the words) or people it would not add (named in the line, still picked).
 */
export const readAgainAfter = (err: unknown, what: 'rename' | 'add' | 'remove'): boolean => {
  if (what === 'rename') return statusOf(err) !== 400;
  if (what === 'add') {
    return (err as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code !== 'members_not_allowed';
  }
  return true;
};

// Where the keyboard goes after a change: a control by its data-focus name, or one
// member's remove button ("remove:<code>").
const AT = {
  rename: 'rename',
  name: 'name',
  members: 'members',
  add: 'add',
  leave: 'leave',
  confirm: 'confirm-cancel',
  addGo: 'add-go',
  addCancel: 'add-cancel',
};
const removeAt = (code: string): string => `remove:${code}`;
const spotIn = (box: HTMLElement, spot: string): HTMLElement | null => {
  let el: HTMLElement | null;
  if (spot.startsWith('remove:')) {
    const code = spot.slice('remove:'.length);
    el = Array.from(box.querySelectorAll<HTMLElement>('[data-remove]')).find((one) => one.dataset.remove === code) ?? null;
  } else {
    el = box.querySelector<HTMLElement>(`[data-focus="${spot}"]`);
  }
  // A control that is off cannot take the keyboard (Add people once the group is full).
  return el && !(el as HTMLButtonElement).disabled ? el : null;
};

const TAG = 'flex-none rounded-full px-1.5 py-px text-[11px] font-semibold';
const ICON_BUTTON = 'flex h-8 w-8 flex-none items-center justify-center rounded-lg text-gray-400 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-40 disabled:hover:bg-transparent';
const QUIET_BUTTON = 'flex h-9 items-center rounded-xl px-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50';
const MAIN_BUTTON = 'flex h-9 items-center rounded-xl px-4 text-sm font-semibold text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50';
const TRY_AGAIN = 'font-semibold text-primary-600 hover:text-primary-700 focus:outline-none focus-visible:underline';

export default function GroupInfoDialog({ groupId, projectId, me, onClose, onChanged, onLeft }: GroupInfoDialogProps): ReactElement {
  const [detail, setDetail] = useState<GroupDetail | null>(null);
  const [load, setLoad] = useState<Load>('loading');
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<PickPerson[]>([]);
  /** The name being typed, or null when not renaming. */
  const [draft, setDraft] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [busy, setBusy] = useState(false);
  const [line, setLine] = useState<string | null>(null);

  const questionId = useId();
  const root = useRef<HTMLDivElement>(null);
  const alive = useRef(true);
  // The ref stops a second click that lands before the first has redrawn the controls.
  const busyNow = useRef(false);
  const loads = useRef(0);
  // Where the keyboard goes once the screen has caught up with a change.
  const focusNext = useRef<{ at: string; or: string } | null>(null);
  const changed = useRef(onChanged);
  const left = useRef(onLeft);
  useEffect(() => {
    changed.current = onChanged;
    left.current = onLeft;
  }, [onChanged, onLeft]);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const focusSoon = (at: string, or: string = AT.members) => { focusNext.current = { at, or }; };
  useEffect(() => {
    const want = focusNext.current;
    if (!want || busy || !root.current) return;
    // Neither there (the group is no longer this person's): the pop-up itself, so the
    // keyboard never falls out of it to the page behind.
    const el = spotIn(root.current, want.at) ?? spotIn(root.current, want.or) ?? root.current;
    focusNext.current = null;
    el.focus();
  });

  /** Read the group. `tell`: a group that is no longer this person's is told to the pane and the list too. */
  const read = useCallback(async (tell = true) => {
    const round = ++loads.current;
    try {
      const res = await messagesAPI.groupInfo(groupId);
      if (!alive.current || round !== loads.current) return;
      const got = res?.data?.data;
      if (!got || !Array.isArray(got.members)) {
        setLoad('failed');
        return;
      }
      // Messages are per project: a group of another project is not this page's to show.
      if (Number(got.projectId) !== projectId) {
        setDetail(null);
        setLoad('missing');
        return;
      }
      setDetail(got);
      setLoad('ready');
    } catch (err) {
      if (!alive.current || round !== loads.current) return;
      const status = statusOf(err);
      if (status === 404 || status === 403) {
        // Not this person's any more (left on the phone, removed, the tick gone): the
        // conversation and the list find that out too.
        setDetail(null);
        setLoad('missing');
        if (tell) changed.current(null);
      } else {
        // A group on screen stays on screen; only a first read says it failed.
        setLoad((was) => (was === 'ready' ? was : status === 401 ? 'unconfirmed' : 'failed'));
      }
    }
  }, [groupId, projectId]);

  useEffect(() => { void read(); }, [read]);

  const retry = () => {
    setLoad('loading');
    void read();
  };

  /** Run one change. True when the server made it; the group it answers with is shown. */
  const change = async (
    what: 'rename' | 'add' | 'remove',
    call: () => Promise<{ data: { data?: GroupDetail } }>,
    failed: string,
    names: PickPerson[] = [],
  ): Promise<boolean> => {
    if (busyNow.current) return false;
    busyNow.current = true;
    setBusy(true);
    setLine(null);
    try {
      const res = await call();
      if (!alive.current) return false;
      const got = res?.data?.data;
      if (got && Array.isArray(got.members)) setDetail(got);
      else void read();
      changed.current(got && Array.isArray(got.members) ? got : null);
      return true;
    } catch (err) {
      if (!alive.current) return false;
      setLine(actionRefusal(err, failed, names));
      if (readAgainAfter(err, what)) {
        // The server knows better than the panel: the conversation and the list read
        // again, and the group too, before the controls come back (someone already
        // removed is no longer listed; an admin no more sees no admin controls).
        changed.current(null);
        await read(false);
      }
      return false;
    } finally {
      busyNow.current = false;
      if (alive.current) setBusy(false);
    }
  };

  const isAdmin = detail?.myRole === 'admin';
  const room = detail ? Math.max(0, GROUP_LIMITS.MAX_PEOPLE - detail.memberCount) : 0;
  // An admin no more (the panel read the group again) is not adding, renaming or removing anyone.
  const pickingPeople = adding && isAdmin;
  const renaming = draft !== null && isAdmin;
  const asking = confirm && (confirm.kind === 'leave' || isAdmin) ? confirm : null;

  // ── Rename ──────────────────────────────────────────────────────────────────
  const startRename = () => {
    if (!detail || busy) return;
    setLine(null);
    setConfirm(null);
    setDraft(detail.title);
    focusSoon(AT.name);
  };
  const stopRename = () => {
    setDraft(null);
    focusSoon(AT.rename);
  };
  const saveName = async (e?: FormEvent) => {
    e?.preventDefault();
    if (draft === null || !detail) return;
    const name = cleanTitle(draft);
    if (!name) return;
    if (name === detail.title) {
      stopRename();
      return;
    }
    if (await change('rename', () => messagesAPI.renameGroup(groupId, name), 'The name could not be saved. Try again.')) stopRename();
    // Refused: back to the name, or to the people when renaming is no longer this person's.
    else if (alive.current) focusSoon(AT.name);
  };
  const onNameKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Escape' || e.nativeEvent.isComposing) return;
    // Escape here stops the rename. The pop-up stays (MessagesDialog skips an Escape that is used).
    e.preventDefault();
    if (!busy) stopRename();
  };

  // ── Remove and leave: asked first, in the panel ──────────────────────────────
  const ask = (what: Confirm) => {
    if (busy) return;
    setLine(null);
    setDraft(null);
    setConfirm(what);
    focusSoon(AT.confirm);
  };
  const back = (what: Confirm) => (what.kind === 'remove' ? removeAt(what.member.empCode) : AT.leave);
  const cancelConfirm = () => {
    if (!confirm || busy) return;
    focusSoon(back(confirm), confirm.kind === 'leave' ? AT.members : AT.leave);
    setConfirm(null);
  };

  const remove = async (member: GroupMember) => {
    const done = await change('remove', () => messagesAPI.removeFromGroup(groupId, member.empCode), 'Could not remove. Try again.');
    if (!alive.current) return;
    setConfirm(null);
    focusSoon(done ? AT.members : removeAt(member.empCode));
  };

  const leave = async () => {
    if (busyNow.current) return;
    busyNow.current = true;
    setBusy(true);
    setLine(null);
    let out = false;
    try {
      await messagesAPI.leaveGroup(groupId);
      out = true;
    } catch (err) {
      // Not found: this person is out of the group already (left on the phone, removed).
      if (statusOf(err) === 404) out = true;
      else if (alive.current) {
        setLine(actionRefusal(err, 'Could not leave. Try again.'));
        setConfirm(null);
        focusSoon(AT.leave);
      }
    } finally {
      busyNow.current = false;
      if (alive.current) setBusy(false);
    }
    // What the person asked for: the page closes the group and reads the list again.
    if (out && alive.current) left.current();
  };

  // ── Add people ──────────────────────────────────────────────────────────────
  const startAdding = () => {
    if (busy || room === 0) return;
    setLine(null);
    setConfirm(null);
    setDraft(null);
    setPicked([]);
    setAdding(true);
  };
  const stopAdding = () => {
    setAdding(false);
    setPicked([]);
    setLine(null);
    focusSoon(AT.add);
  };
  const toggle = (person: PickPerson) => {
    if (busy) return;
    setLine(null);
    setPicked((was) => {
      if (was.some((p) => p.empCode === person.empCode)) return was.filter((p) => p.empCode !== person.empCode);
      return was.length >= room ? was : [...was, person];
    });
  };
  const add = async () => {
    if (picked.length === 0 || picked.length > room) return;
    const chosen = picked;
    if (await change('add', () => messagesAPI.addToGroup(groupId, chosen.map((p) => p.empCode)), 'The people could not be added. Try again.', chosen)) {
      stopAdding();
    } else if (alive.current) {
      focusSoon(AT.addGo, AT.addCancel);
    }
  };

  // ── What is drawn ───────────────────────────────────────────────────────────
  let body: ReactElement;
  if (!detail) {
    if (load === 'loading') body = <InlineLoader label="Loading…" className="flex justify-center py-8" />;
    else if (load === 'missing') body = <Quiet>{NOT_AVAILABLE}</Quiet>;
    else {
      body = (
        <Quiet>
          {load === 'unconfirmed' ? SIGN_IN : 'This group could not be loaded.'}{' '}
          <button type="button" onClick={retry} className={TRY_AGAIN}>Try again</button>
        </Quiet>
      );
    }
    body = <div className="px-2 pb-4">{body}</div>;
  } else if (pickingPeople) {
    body = (
      <AddPeople
        projectId={projectId}
        groupId={groupId}
        room={room}
        picked={picked}
        busy={busy}
        line={line}
        onToggle={toggle}
        onCancel={stopAdding}
        onAdd={() => { void add(); }}
      />
    );
  } else {
    const confirmWords = asking ? (asking.kind === 'leave' ? 'Leave this group?' : `Remove ${asking.member.name}?`) : '';
    body = (
      <>
        <div className="flex-none px-5 pb-3">
          {!renaming || draft === null ? (
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-primary-50 text-primary-700">
                <Users className="h-5 w-5" />
              </span>
              <p className="min-w-0 flex-1 break-words text-base font-semibold text-gray-900">{detail.title}</p>
              {isAdmin && (
                <button
                  type="button"
                  data-focus="rename"
                  onClick={startRename}
                  disabled={busy}
                  aria-label="Rename group"
                  title="Rename"
                  className={`${ICON_BUTTON} hover:bg-gray-100 hover:text-gray-700`}
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
          ) : (
            <form onSubmit={(e) => { void saveName(e); }} className="space-y-2">
              <div className="relative">
                <input
                  data-focus="name"
                  type="text"
                  aria-label="Group name"
                  placeholder="Group name"
                  autoComplete="off"
                  maxLength={GROUP_LIMITS.TITLE_CHARS}
                  value={draft}
                  disabled={busy}
                  onKeyDown={onNameKey}
                  onChange={(e) => {
                    // Cut here as well: pasted or scripted text can get past maxLength.
                    setDraft(e.target.value.slice(0, GROUP_LIMITS.TITLE_CHARS));
                    setLine(null);
                  }}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-14 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-gray-50"
                />
                {/* Said only near the limit, where it is useful. */}
                {draft.length >= GROUP_LIMITS.TITLE_CHARS - 10 && (
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    {draft.length}/{GROUP_LIMITS.TITLE_CHARS}
                  </span>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={stopRename} disabled={busy} className={QUIET_BUTTON}>Cancel</button>
                <button
                  type="submit"
                  disabled={busy || !cleanTitle(draft)}
                  className={`${MAIN_BUTTON} bg-primary-600 hover:bg-primary-700 focus-visible:ring-primary-500`}
                >
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="flex flex-none items-center justify-between gap-3 border-t border-gray-100 px-5 pb-1 pt-3">
          <h3 data-focus="members" tabIndex={-1} className="text-xs font-medium text-gray-500 focus:outline-none">
            {membersLabel(detail.memberCount)}
          </h3>
          {isAdmin && (
            <button
              type="button"
              data-focus="add"
              onClick={startAdding}
              disabled={busy || room === 0}
              title={room === 0 ? `A group holds ${GROUP_LIMITS.MAX_PEOPLE} people.` : undefined}
              className="flex h-8 items-center gap-1.5 rounded-lg px-2 text-sm font-semibold text-primary-600 transition-colors hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              Add people
            </button>
          )}
        </div>

        <ul aria-label="Members" className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pb-2">
          {detail.members.map((member) => {
            const mine = sameCode(member.empCode, me);
            const asked = asking?.kind === 'remove' && asking.member.empCode === member.empCode;
            return (
              <li key={member.empCode} className={`flex items-center gap-3 rounded-xl px-3 py-2 ${asked ? 'bg-red-50' : ''}`}>
                <Face name={member.name} />
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-sm font-medium text-gray-900">{member.name}</span>
                    {mine && <span className={`${TAG} bg-gray-100 text-gray-600`}>You</span>}
                    {member.role === 'admin' && <span className={`${TAG} bg-primary-50 text-primary-700`}>Admin</span>}
                  </span>
                  <span className="block truncate text-xs text-gray-500">{pickLine(member)}</span>
                </span>
                {/* Never on the admin's own row: leaving is "Leave group", below. */}
                {isAdmin && !mine && (
                  <button
                    type="button"
                    data-remove={member.empCode}
                    onClick={() => ask({ kind: 'remove', member })}
                    disabled={busy}
                    aria-label={`Remove ${member.name}`}
                    title="Remove from the group"
                    className={`${ICON_BUTTON} hover:bg-red-50 hover:text-red-600`}
                  >
                    <UserMinus className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>

        <div className="flex-none border-t border-gray-100 px-5 py-3">
          {line && <p role="alert" className="mb-2 text-sm text-red-600">{line}</p>}
          {asking ? (
            <div role="group" aria-labelledby={questionId} className="flex flex-wrap items-center justify-end gap-2">
              <p id={questionId} className="mr-auto min-w-0 text-sm font-semibold text-gray-900">{confirmWords}</p>
              <button type="button" data-focus="confirm-cancel" onClick={cancelConfirm} disabled={busy} className={QUIET_BUTTON}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (asking.kind === 'leave') void leave();
                  else void remove(asking.member);
                }}
                disabled={busy}
                title={asking.kind === 'leave' ? 'You will stop getting its messages.' : 'They will no longer see this group.'}
                className={`${MAIN_BUTTON} bg-red-600 hover:bg-red-700 focus-visible:ring-red-500`}
              >
                {asking.kind === 'leave' ? (busy ? 'Leaving…' : 'Leave') : (busy ? 'Removing…' : 'Remove')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-focus="leave"
              onClick={() => ask({ kind: 'leave' })}
              disabled={busy}
              className="flex h-9 items-center gap-1.5 rounded-lg px-1 text-sm font-semibold text-red-600 transition-colors hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-50"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              Leave group
            </button>
          )}
        </div>
      </>
    );
  }

  return (
    <MessagesDialog title={pickingPeople ? 'Add people' : 'Group info'} onClose={onClose} initialFocus={root} busy={busy}>
      {/* Focusable from code only: the keyboard lands here when the pop-up opens, while the group is read. */}
      <div ref={root} tabIndex={-1} className="flex min-h-0 flex-1 flex-col focus:outline-none">
        {body}
      </div>
    </MessagesDialog>
  );
}

/** "Add people": the group picker with this group's id, so the people in it are left out. */
function AddPeople({ projectId, groupId, room, picked, busy, line, onToggle, onCancel, onAdd }: {
  projectId: number;
  groupId: string;
  /** How many more people the group holds. */
  room: number;
  picked: PickPerson[];
  busy: boolean;
  line: string | null;
  onToggle: (person: PickPerson) => void;
  onCancel: () => void;
  onAdd: () => void;
}): ReactElement {
  const [typed, setTyped] = useState('');
  const findBox = useRef<HTMLInputElement>(null);
  useEffect(() => { findBox.current?.focus(); }, []);

  const ask = useCallback(
    (inProject: number, q: string, signal?: AbortSignal) => messagesAPI.peopleToAdd(inProject, groupId, q, signal),
    [groupId],
  );
  const { people, loading, failure, searching, retry } = usePeopleFinder(projectId, typed, ask);

  const full = picked.length >= room;
  // The group filled up elsewhere since these were picked: some come out before Add.
  const tooMany = picked.length > room;
  const isPicked = (person: PickPerson) => picked.some((p) => p.empCode === person.empCode);
  // With nothing searched, people picked from an earlier search come first, so they can
  // always be seen and taken out. A search shows its own results only.
  const listed = new Set(people.map((p) => p.empCode));
  const rows = searching ? people : [...picked.filter((p) => !listed.has(p.empCode)), ...people];

  let list: ReactElement;
  if (failure) {
    list = <FindFailureLine failure={failure} off="Adding people is not available here." onRetry={retry} />;
  } else if (rows.length === 0) {
    list = loading
      ? <InlineLoader label="Loading people…" className="flex justify-center py-6" />
      : <Quiet>{searching ? 'No one by that name.' : 'Type a name to find someone.'}</Quiet>;
  } else {
    list = (
      <>
        <ListHeading searching={searching} />
        <ul aria-label="People" className="space-y-0.5">
          {rows.map((person) => {
            const on = isPicked(person);
            return (
              <li key={person.empCode}>
                <TickRow
                  person={person}
                  on={on}
                  onToggle={() => onToggle(person)}
                  // No room left: the people already picked can still be taken out.
                  disabled={busy || (!on && full)}
                  title={!on && full ? `A group holds ${GROUP_LIMITS.MAX_PEOPLE} people.` : undefined}
                />
              </li>
            );
          })}
        </ul>
      </>
    );
  }

  return (
    <>
      <div className="flex-none space-y-2 px-5 pb-3">
        <FindField inputRef={findBox} value={typed} onChange={setTyped} busy={loading && rows.length > 0} disabled={busy} />
        <PickedChips picked={picked} disabled={busy} onUnpick={onToggle} />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">{list}</div>
      <div className="flex-none border-t border-gray-100 px-5 py-3">
        {line && <p role="alert" className="mb-2 text-sm text-red-600">{line}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" data-focus="add-cancel" onClick={onCancel} disabled={busy} className={QUIET_BUTTON}>Cancel</button>
          <button
            type="button"
            data-focus="add-go"
            onClick={onAdd}
            disabled={busy || picked.length === 0 || tooMany}
            title={busy ? undefined : picked.length === 0 ? 'Pick at least one person.' : tooMany ? `A group holds ${GROUP_LIMITS.MAX_PEOPLE} people.` : undefined}
            className={`${MAIN_BUTTON} bg-primary-600 hover:bg-primary-700 focus-visible:ring-primary-500`}
          >
            {busy ? 'Adding…' : 'Add'}
          </button>
        </div>
      </div>
    </>
  );
}
